import { useCallback, useEffect, useState } from 'react';
import { Loader2, CalendarClock, CheckCircle2, UserX, XCircle, Clock, Users, Timer } from 'lucide-react';
import { DateInput } from '@/components/ui/DateInput';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin demo report — aggregate summary of all demos + per-advisor
// punctuality (host join delay vs scheduled start, from Zoom webhooks).
// ────────────────────────────────────────────────────────────────────────────

interface Advisor {
  advisorId: string;
  advisorName: string;
  total: number;
  completed: number;
  noShow: number;
  hostJoined: number;
  avgHostDelayMin: number;
  onTimePercent: number;
}
interface Report {
  lateThresholdMin: number;
  overview: {
    total: number; upcoming: number; completed: number; cancelled: number;
    noShow: number; attended: number; attendanceRate: number; onTimeRate: number;
  };
  advisors: Advisor[];
}

export function SuperAdminDemoReport() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const { data } = await saApi.get('/super-admin/demo/report', { params });
      setData(data);
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const o = data?.overview;

  return (
    <div className="space-y-4">
      {/* Date range */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-normal text-slate-500 uppercase mb-1">From</label>
          <DateInput value={dateFrom} onChange={setDateFrom} className="w-40" />
        </div>
        <div>
          <label className="block text-[10px] font-normal text-slate-500 uppercase mb-1">To</label>
          <DateInput value={dateTo} onChange={setDateTo} className="w-40" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50">
            Clear
          </button>
        )}
        <span className="text-xs text-slate-400 ml-auto">All demos{dateFrom || dateTo ? ' in range' : ''}</span>
      </div>

      {loading || !o ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" /></div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile icon={CalendarClock} label="Total demos" value={o.total} tone="indigo" />
            <Tile icon={Clock} label="Upcoming" value={o.upcoming} tone="blue" />
            <Tile icon={CheckCircle2} label="Completed" value={o.completed} tone="emerald" />
            <Tile icon={UserX} label="No-show" value={o.noShow} tone="amber" />
            <Tile icon={XCircle} label="Cancelled" value={o.cancelled} tone="rose" />
            <Tile icon={Users} label="Attendance rate" value={`${o.attendanceRate}%`} tone="emerald" sub={`${o.attended} attended`} />
            <Tile icon={Timer} label="On-time rate" value={`${o.onTimeRate}%`} tone="indigo" sub={`advisor ≤ ${data!.lateThresholdMin}m late`} />
          </div>

          {/* Per-advisor table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800">By advisor</h3>
              <p className="text-xs text-slate-400 mt-0.5">Punctuality = host join time vs scheduled start (from Zoom). Late &gt; {data!.lateThresholdMin} min.</p>
            </div>
            {data!.advisors.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10">No demos in this range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase border-b border-slate-100">
                      <th className="px-4 py-2">Advisor</th>
                      <th className="px-4 py-2 text-right">Total</th>
                      <th className="px-4 py-2 text-right">Completed</th>
                      <th className="px-4 py-2 text-right">No-show</th>
                      <th className="px-4 py-2 text-right">Avg host delay</th>
                      <th className="px-4 py-2 text-right">On-time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data!.advisors.map(a => (
                      <tr key={a.advisorId} className="hover:bg-slate-50/60">
                        <td className="px-4 py-2.5 font-medium text-slate-800">{a.advisorName}</td>
                        <td className="px-4 py-2.5 text-right text-slate-700">{a.total}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-600 font-medium">{a.completed}</td>
                        <td className="px-4 py-2.5 text-right text-amber-600 font-medium">{a.noShow}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">
                          {a.hostJoined ? `${a.avgHostDelayMin}m` : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {a.hostJoined ? (
                            <span className={`font-medium ${a.onTimePercent >= 80 ? 'text-emerald-600' : a.onTimePercent >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>{a.onTimePercent}%</span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-400">Attendance &amp; punctuality data is populated by the Zoom webhook (Demos → Bookings → setup card). Demos without webhook data are counted by status only.</p>
        </>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone, sub }: {
  icon: any; label: string; value: string | number; tone: 'indigo' | 'blue' | 'emerald' | 'amber' | 'rose'; sub?: string;
}) {
  const map = {
    indigo: 'text-[#6366F1] bg-indigo-50',
    blue: 'text-blue-600 bg-blue-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    rose: 'text-rose-600 bg-rose-50',
  }[tone];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg ${map}`}><Icon className="h-4 w-4" /></span>
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
